import type { HeadsetTrouble } from '@vrlearning/lesson-schema';

/**
 * Remembering what went wrong, until somebody can be told.
 *
 * The most useful failure to report is being unable to reach the API, and a
 * headset that cannot reach the API cannot report anything. So a failure is
 * written down locally and carried on the **next check-in that gets through**:
 * "last time I managed to reach you, here is what had gone wrong since".
 *
 * Kept in localStorage rather than memory because the failures worth hearing
 * about are the ones that end with somebody restarting the headset. It is the
 * same storage the pairing lives in, so it survives exactly as well -- and
 * losing a complaint is not worth a single line of recovery code.
 *
 * One at a time, and the **first** one wins. A headset that cannot reach the
 * server then fails at loading a lesson, and at its media, and at its socket;
 * reporting the last of those would point support at the wrong thing
 * entirely. Whatever broke first is the thing that explains the rest, so it
 * is kept until it has been reported.
 */

/** Cleared with the rest of a school's data when the pairing ends (school-data.ts). */
export const TROUBLE_KEY = 'vrlearning.trouble';

export type Trouble = {
  readonly code: HeadsetTrouble;
  /** A short clue: an HTTP status, an error name. Never a stack trace. */
  readonly note?: string;
};

function read(): Trouble | undefined {
  try {
    const raw = localStorage.getItem(TROUBLE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<Trouble>;
    return typeof parsed.code === 'string' ? (parsed as Trouble) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Notes that something failed.
 *
 * Never throws and never blocks: this is called from catch blocks in the
 * middle of a lesson going wrong, and a failure to record a failure must not
 * become the thing the student notices.
 */
export function rememberTrouble(code: HeadsetTrouble, note?: string): void {
  try {
    // The first failure explains the rest: being unable to reach the API
    // makes everything after it fail too, and the last of those would point
    // support at the wrong thing.
    if (read()) return;
    localStorage.setItem(
      TROUBLE_KEY,
      JSON.stringify(note === undefined ? { code } : { code, note }),
    );
  } catch {
    // Storage refused. Nothing to do: the one thing we could report is the
    // thing we cannot write down.
  }
}

/** What to send on the next check-in, if anything. */
export function troubleToReport(): Trouble | undefined {
  return read();
}

/**
 * Forgets the complaint, once the server has been told.
 *
 * Only after a check-in that succeeded: clearing on the attempt would lose
 * exactly the reports that matter, the ones made while nothing was getting
 * through.
 */
export function forgetTrouble(): void {
  try {
    localStorage.removeItem(TROUBLE_KEY);
  } catch {
    // Then it will be reported twice, which is harmless.
  }
}
